import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  BookOpen,
  AlertCircle,
  Users,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { GoGraph } from 'react-icons/go';
import { MdOutlineArrowOutward } from 'react-icons/md';
import { BsCheckCircle, BsCardChecklist } from 'react-icons/bs';
import useInstructorDashboardStore from '@/stores/InstructorStore/InstructorDashboardStore';
import { LoadingSpinner, ErrorDisplay, DateRangePicker, ConfirmationModal } from '@/Components/common';
import { TaskActionType } from '@/services/PagesServices/InstructorDashboardService';
import './TrainerDashboard.css';

interface StatCardProps {
  title: string;
  value: number;
  increase: string;
  icon: React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, increase, icon, loading, onClick }) => (
  <div
    className={`trainer-dashboard-page-stat-card${onClick ? ' trainer-dashboard-page-stat-card--clickable' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
  >
    {loading ? (
      <div className="trainer-dashboard-page-stat-loading">
        <LoadingSpinner size="sm" />
      </div>
    ) : (
      <>
        <div className="trainer-dashboard-page-stat-header">
          <div>
            <div className="trainer-dashboard-page-stat-title">{title}</div>
            <div className="trainer-dashboard-page-stat-value">{value.toLocaleString()}</div>
          </div>
          <div className="trainer-dashboard-page-stat-icon">{icon}</div>
        </div>
        <div className="trainer-dashboard-page-stat-increase">+ {increase} Increased from Last week</div>
      </>
    )}
  </div>
);

// Maps each stat card to the page it should navigate to when clicked.
const STAT_CARD_TARGETS: Record<string, string> = {
  'Total Students': '/trainer/users/user-list',
  'Active Courses': '/trainer/content/course-management',
  'Pending Reviews': '/trainer/users/learning-assignment',
  'Total Enrollments': '/trainer/content/course-management',
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    stats,
    courses,
    tasks,
    learningHoursData,
    learningProgress,
    dateRange,
    loadingStats,
    loadingCourses,
    loadingTasks,
    loadingLearningHours,
    loadingProgress,
    taskActionLoading,
    errorStats,
    errorCourses,
    errorTasks,
    errorLearningHours,
    errorProgress,
    fetchStats,
    fetchCourses,
    fetchTasks,
    fetchLearningHoursData,
    fetchLearningProgress,
    fetchAllData,
    setDateRange,
    handleTaskAction,
  } = useInstructorDashboardStore();

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    taskId: string;
    action: TaskActionType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    taskId: '',
    action: 'approve',
    title: '',
    message: '',
  });

  // Toast notification state
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  // Tooltip state for chart hover
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    x: number;
    y: number;
    hours: string;
    week: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    hours: '',
    week: '',
  });

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Calculate SVG chart points from learning hours data
  const chartPoints = useMemo(() => {
    if (!learningHoursData || learningHoursData.length === 0) {
      return '';
    }

    const maxHours = Math.max(...learningHoursData.map((d) => d.hours), 1);
    const width = 500;
    const height = 180;
    const padding = 10;

    return learningHoursData
      .map((data, index) => {
        const x = padding + (index / (learningHoursData.length - 1)) * (width - 2 * padding);
        const y = height - padding - (data.hours / maxHours) * (height - 2 * padding);
        return `${x},${y}`;
      })
      .join(' ');
  }, [learningHoursData]);

  // Calculate individual data points for hover interactions
  const dataPoints = useMemo(() => {
    if (!learningHoursData || learningHoursData.length === 0) {
      return [];
    }

    const maxHours = Math.max(...learningHoursData.map((d) => d.hours), 1);
    const width = 500;
    const height = 180;
    const padding = 10;

    return learningHoursData.map((data, index) => {
      const x = padding + (index / (learningHoursData.length - 1)) * (width - 2 * padding);
      const y = height - padding - (data.hours / maxHours) * (height - 2 * padding);
      return { x, y, hours: data.hours, week: data.week };
    });
  }, [learningHoursData]);

  // Calculate Y-axis labels dynamically
  const yAxisLabels = useMemo(() => {
    if (!learningHoursData || learningHoursData.length === 0) {
      return [40, 30, 20, 10];
    }
    const maxHours = Math.max(...learningHoursData.map((d) => d.hours));
    const step = Math.ceil(maxHours / 4);
    return [step * 4, step * 3, step * 2, step].map((v) => Math.min(v, maxHours + 5));
  }, [learningHoursData]);

  // Get highlighted point (last data point)
  const highlightedPoint = useMemo(() => {
    if (!learningHoursData || learningHoursData.length === 0 || !chartPoints) {
      return null;
    }
    const points = chartPoints.split(' ');
    const lastPoint = points[points.length - 1];
    if (!lastPoint) return null;
    const [x, y] = lastPoint.split(',').map(Number);
    return { x, y, hours: learningHoursData[learningHoursData.length - 1].hours };
  }, [chartPoints, learningHoursData]);

  // Handle chart point hover
  const handlePointHover = (event: React.MouseEvent<SVGCircleElement>, hours: number, week: string) => {
    const circle = event.currentTarget;
    const cx = parseFloat(circle.getAttribute('cx') || '0');
    const cy = parseFloat(circle.getAttribute('cy') || '0');

    setTooltipData({
      visible: true,
      x: cx,
      y: cy - 50,
      hours: `${hours} Hours`,
      week,
    });
  };

  // Handle chart point leave
  const handlePointLeave = () => {
    setTooltipData({
      visible: false,
      x: 0,
      y: 0,
      hours: '',
      week: '',
    });
  };

  const getIconByType = (iconType: string) => {
    switch (iconType) {
      case 'book':
        return <BookOpen size={24} />;
      case 'alert':
        return <AlertCircle size={24} />;
      case 'check':
        return <BsCheckCircle size={24} />;
      case 'users':
        return <Users size={24} />;
      default:
        return <BookOpen size={24} />;
    }
  };

  // Handle task action click
  const onTaskActionClick = (taskId: string, action: string) => {
    const taskActionMap: Record<string, TaskActionType> = {
      'Review': 'approve',
      'Grade': 'approve',
      'Provide Feedback': 'complete',
      'Respond': 'complete',
      'Approve': 'approve',
    };

    const actionType = taskActionMap[action] || 'approve';

    // For reject action, show confirmation
    if (actionType === 'reject') {
      setConfirmModal({
        isOpen: true,
        taskId,
        action: actionType,
        title: 'Reject Task',
        message: 'Are you sure you want to reject this task? This action cannot be undone.',
      });
    } else {
      // For approve/remind/complete, execute directly
      executeTaskAction(taskId, actionType);
    }
  };

  // Execute task action
  const executeTaskAction = async (taskId: string, action: TaskActionType) => {
    const result = await handleTaskAction(taskId, action);

    setToast({
      show: true,
      message: result.message || (result.success ? 'Action completed' : 'Action failed'),
      type: result.success ? 'success' : 'error',
    });

    // Hide toast after 3 seconds
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Handle confirmation modal confirm
  const onConfirmAction = () => {
    executeTaskAction(confirmModal.taskId, confirmModal.action);
    setConfirmModal({ ...confirmModal, isOpen: false });
  };

  // Handle date range change
  const onDateRangeApply = (startDate: Date, endDate: Date) => {
    setDateRange(startDate, endDate);
  };

  // Navigate to Learning Assignment Tool page
  const onViewDetailsClick = () => {
    navigate('/trainer/users/learning-assignment');
  };

  // Check if initial load is happening
  const isInitialLoading =
    loadingStats && loadingCourses && loadingTasks && loadingLearningHours && loadingProgress;

  // Check if there's a critical error (all failed)
  const hasCriticalError =
    errorStats && errorCourses && errorTasks && errorLearningHours && errorProgress;

  if (isInitialLoading) {
    return (
      <div className="trainer-dashboard-page trainer-dashboard-page--loading">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (hasCriticalError) {
    return (
      <div className="trainer-dashboard-page">
        <ErrorDisplay
          title="Failed to Load Dashboard Data"
          message="We couldn't load the dashboard. Please check your connection and try again."
          type="error"
          showRetry
          onRetry={fetchAllData}
        />
      </div>
    );
  }

  return (
    <div className="trainer-dashboard-page">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`trainer-dashboard-toast trainer-dashboard-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="trainer-dashboard-page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Your Mkraft teaching overview — track your courses and learner progress.</p>
        </div>
        <DateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onApply={onDateRangeApply}
        />
      </div>

      {/* Stats Grid */}
      <div className="trainer-dashboard-page-stats-grid">
        {loadingStats ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="trainer-dashboard-page-stat-card trainer-dashboard-page-stat-card--skeleton">
                <LoadingSpinner size="sm" />
              </div>
            ))}
          </>
        ) : errorStats ? (
          <div className="trainer-dashboard-page-section-error">
            <ErrorDisplay
              title="Failed to load stats"
              message={errorStats}
              type="error"
              showRetry
              onRetry={fetchStats}
            />
          </div>
        ) : (
          stats?.map((stat, index) => {
            const target = STAT_CARD_TARGETS[stat.title];
            return (
              <StatCard
                key={index}
                title={stat.title}
                value={stat.value}
                increase={stat.increase}
                icon={getIconByType(stat.iconType)}
                onClick={target ? () => navigate(target) : undefined}
              />
            );
          })
        )}
      </div>

      <div className="trainer-dashboard-page-analytics-banner">
        <div className="trainer-dashboard-page-analytics-icon"><GoGraph /></div>
        <div>
          <div className="trainer-dashboard-page-analytics-title">Analytics & Graphs</div>
          <div className="trainer-dashboard-page-analytics-subtitle">Recommended for you</div>
        </div>
      </div>

      <div className="trainer-dashboard-page-content-grid">
        {/* Learning Hours Card */}
        <div className="trainer-dashboard-page-learning-hours-card">
          {loadingLearningHours || loadingProgress ? (
            <div className="trainer-dashboard-page-section-loading">
              <LoadingSpinner size="md" />
            </div>
          ) : errorLearningHours ? (
            <ErrorDisplay
              title="Failed to load learning hours"
              message={errorLearningHours}
              type="error"
              showRetry
              onRetry={fetchLearningHoursData}
            />
          ) : (
            <div className="trainer-dashboard-page-learning-hours-row">
              {/* Left Section: Chart */}
              <div className="trainer-dashboard-page-chart-section">
                <div className="trainer-dashboard-page-card-header">
                  <h3>Learning Hours Trend</h3>
                  <p>Time Learners spent in daily learnings</p>
                </div>
                <div className="trainer-dashboard-page-chart-wrapper">
                  <div className="trainer-dashboard-page-chart-y-axis">
                    {yAxisLabels.map((label, i) => (
                      <span key={i}>{label}</span>
                    ))}
                  </div>
                  <div className="trainer-dashboard-page-chart-area">
                    <svg viewBox="0 0 500 200" className="trainer-dashboard-page-line-chart">
                      {/* Grid lines */}
                      {[0, 1, 2, 3].map((i) => (
                        <line
                          key={i}
                          x1="10"
                          y1={10 + i * 45}
                          x2="490"
                          y2={10 + i * 45}
                          stroke="#f0f0f0"
                          strokeWidth="1"
                        />
                      ))}
                      {/* Chart line */}
                      {chartPoints && (
                        <polyline
                          points={chartPoints}
                          fill="none"
                          stroke="#ff8c42"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}
                      {/* Data points for hover */}
                      {dataPoints.map((point, index) => (
                        <circle
                          key={index}
                          cx={point.x}
                          cy={point.y}
                          r="6"
                          fill="white"
                          stroke="#ff8c42"
                          strokeWidth="3"
                          className="trainer-dashboard-chart-data-point"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => handlePointHover(e, point.hours, point.week)}
                          onMouseLeave={handlePointLeave}
                        />
                      ))}
                    </svg>
                    {/* Tooltip */}
                    {tooltipData.visible && (
                      <div
                        className="trainer-dashboard-chart-tooltip"
                        style={{
                          position: 'absolute',
                          left: `${tooltipData.x}px`,
                          top: `${tooltipData.y}px`,
                          transform: 'translate(-50%, -100%)',
                          background: 'white',
                          padding: '12px 18px',
                          borderRadius: '12px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                          border: '1px solid #f0f0f0',
                          pointerEvents: 'none',
                          zIndex: 50,
                          minWidth: '120px',
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{tooltipData.week}</div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#222' }}>{tooltipData.hours}</div>
                      </div>
                    )}
                    <div className="trainer-dashboard-page-chart-labels">
                      {learningHoursData?.map((item, i) => (
                        <span key={i}>{item.week}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Section: Progress */}
              <div className="trainer-dashboard-page-progress-section">
                <div className="trainer-dashboard-page-date-button-small">
                  {learningProgress?.currentMonth || 'Loading...'}
                  <Calendar size={14} />
                </div>

                <div className="trainer-dashboard-page-progress-info">
                  <div
                    className={`trainer-dashboard-page-progress-icon ${
                      learningProgress?.trend === 'down' ? 'trainer-dashboard-page-progress-icon--down' : ''
                    }`}
                  >
                    {learningProgress?.trend === 'down' ? <TrendingDown /> : <TrendingUp />}
                  </div>
                  <div>
                    <div className="trainer-dashboard-page-progress-label">Progress</div>
                    <div className="trainer-dashboard-page-progress-value">
                      {learningProgress?.totalHours || 0} Hours{' '}
                      <span
                        className={`trainer-dashboard-page-progress-percent ${
                          learningProgress?.trend === 'down'
                            ? 'trainer-dashboard-page-progress-percent--down'
                            : ''
                        }`}
                      >
                        {learningProgress?.progressPercentage || 0}%{' '}
                        {learningProgress?.trend === 'down' ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Top Courses Card */}
        <div className="trainer-dashboard-page-top-courses-card">
          {loadingCourses ? (
            <div className="trainer-dashboard-page-section-loading">
              <LoadingSpinner size="md" />
            </div>
          ) : errorCourses ? (
            <ErrorDisplay
              title="Failed to load courses"
              message={errorCourses}
              type="error"
              showRetry
              onRetry={fetchCourses}
            />
          ) : (
            <>
              <h3>Top 5 Courses</h3>
              <p>Total Number of Enrolled Learners</p>
              <div className="trainer-dashboard-page-courses-list">
                {courses?.map((course, i) => (
                  <div key={i} className="trainer-dashboard-page-course-item">
                    <div className="trainer-dashboard-page-course-stats">
                      <div className="trainer-dashboard-page-course-name">{course.name}</div>
                      <span className="trainer-dashboard-page-course-count">
                        {course.enrolled} of {course.total}
                      </span>
                    </div>
                    <div className="trainer-dashboard-page-course-progress-bar">
                      <div
                        className="trainer-dashboard-page-course-progress-fill"
                        style={{ width: `${(course.enrolled / course.total) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tasks Card */}
      <div className="trainer-dashboard-page-tasks-card">
        <div className="trainer-dashboard-page-tasks-header">
          <div className="trainer-dashboard-page-tasks-header-left">
            <div className="trainer-dashboard-page-tasks-icon">
              <BsCardChecklist />
            </div>
            <div>
              <h3>My Tasks</h3>
              <p>Overview of My Important Tasks</p>
            </div>
          </div>
          <button className="trainer-dashboard-page-view-details-btn" onClick={onViewDetailsClick}>
            View in Details{' '}
            <span>
              <MdOutlineArrowOutward />
            </span>
          </button>
        </div>

        {loadingTasks ? (
          <div className="trainer-dashboard-page-section-loading">
            <LoadingSpinner size="md" />
          </div>
        ) : errorTasks ? (
          <ErrorDisplay
            title="Failed to load tasks"
            message={errorTasks}
            type="error"
            showRetry
            onRetry={fetchTasks}
          />
        ) : tasks && tasks.length === 0 ? (
          <div className="trainer-dashboard-page-empty-tasks">
            <BsCheckCircle size={48} />
            <h4>All caught up!</h4>
            <p>You have no pending tasks</p>
          </div>
        ) : (
          <table className="trainer-dashboard-page-tasks-table">
            <thead>
              <tr>
                <th className="trainer-dashboard-page-task-dot-column"></th>
                <th>Task Description</th>
                <th>Due Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tasks?.map((task) => (
                <tr key={task.id}>
                  <td className="trainer-dashboard-page-task-dot-cell">
                    <div
                      className={`dashboard-page-task-dot ${
                        task.status === 'Urgent'
                          ? 'dashboard-page-task-dot--urgent'
                          : 'dashboard-page-task-dot--pending'
                      }`}
                    />
                  </td>
                  <td>
                    <div className="trainer-dashboard-page-task-description">
                      <div>
                        <div className="trainer-dashboard-page-task-type">
                          {task.type}{' '}
                          <span
                            className={`dashboard-page-task-status dashboard-page-task-status--${task.status.toLowerCase()}`}
                          >
                            {task.status}
                          </span>
                        </div>
                        <div className="trainer-dashboard-page-task-detail">{task.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="trainer-dashboard-page-task-date">{task.dueDate}</td>
                  <td className="trainer-dashboard-page-task-action">
                    <button
                      className="trainer-dashboard-page-action-button"
                      onClick={() => onTaskActionClick(task.id, task.action)}
                      disabled={taskActionLoading[task.id]}
                    >
                      {taskActionLoading[task.id] ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        task.action
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={onConfirmAction}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        confirmButtonStyle="danger"
      />
    </div>
  );
};

export default DashboardPage;
